'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { AUTOMATIONS_ICON } from '#pplx/frontend/apps/ask/automations-shared/constants.js';
import { SidebarHoverCard } from '#pplx/frontend/apps/ask/layout/sidebar/hover-card/SidebarHoverCard.js';
import { SidebarHoverCardProjectLabel } from '#pplx/frontend/apps/ask/layout/sidebar/hover-card/SidebarHoverCardProjectLabel.js';
import { SIDEBAR_HOVER_CARD_PREVIEW_LIMIT, useSidebarHoverCardOpen, useSidebarHoverCardRelativeTime, } from '#pplx/frontend/apps/ask/layout/sidebar/hover-card/sidebarHoverCardState.js';
import { cleanMarkdown } from '#pplx/frontend/apps/ask/markdown/markdown.js';
import { getThreadByUUID } from '#pplx/frontend/apps/ask/queries/entry-queries/getThreadByUUID.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
const threadTypeMessages = defineMessages({
    automation: {
        defaultMessage: 'Automation',
        description: 'Automation type shown in a sidebar session hover popover',
        id: '3ed6dc189b',
    },
    computer: {
        defaultMessage: 'Computer',
        description: 'Thread type shown in a sidebar session hover popover',
        id: '8776cc5033',
    },
    search: {
        defaultMessage: 'Search',
        description: 'Thread type shown in a sidebar session hover popover',
        id: '734abf243a',
    },
});
function getThreadTypeMessage(isAutomation, isComputerTask) {
    if (isAutomation)
        return threadTypeMessages.automation;
    if (isComputerTask)
        return threadTypeMessages.computer;
    return threadTypeMessages.search;
}
function getThreadTypeIcon(isAutomation, isComputerTask) {
    if (isAutomation)
        return AUTOMATIONS_ICON;
    if (isComputerTask)
        return $icon('custom-computer');
    return $icon('search');
}
const getPinnedThreadHoverDetailsQueryKey = (entryUuid) => ['sidebar-pinned-thread-hover-details', entryUuid, { limit: 1 }];
export const ThreadHoverDetails = React.memo(function ThreadHoverDetails({ isComputerTask, isAutomation = false, updatedAt, answerPreview, projectTitle, projectIcon, detailsLookupEntryUuid, menuIsActive, 
// The remaining props pass straight through to the shared card.
...cardProps }) {
    const { $t } = useIntl();
    const { open: hoverDetailsOpen, onOpenChange } = useSidebarHoverCardOpen(menuIsActive);
    const { data: loadedDetails } = useQuery({
        queryKey: getPinnedThreadHoverDetailsQueryKey(detailsLookupEntryUuid),
        queryFn: async () => {
            if (!detailsLookupEntryUuid)
                throw new Error('Missing pinned thread entry UUID');
            const thread = await getThreadByUUID({
                slugOrUUID: detailsLookupEntryUuid,
                reason: 'sidebar-pinned-thread-hover-details',
                options: { limit: 1 },
            });
            const latestEntry = thread?.entries.at(-1);
            if (!latestEntry)
                throw new Error('Pinned thread has no entries');
            return latestEntry;
        },
        select: (latestEntry) => {
            const collection = latestEntry.collection_info;
            const latestAnswer = latestEntry.text;
            return {
                updatedAt: latestEntry.updated_datetime,
                answerPreview: latestAnswer
                    ? cleanMarkdown(latestAnswer, SIDEBAR_HOVER_CARD_PREVIEW_LIMIT)
                    : undefined,
                projectTitle: collection?.title,
                projectIcon: collection ?? undefined,
            };
        },
        enabled: hoverDetailsOpen && detailsLookupEntryUuid != null,
        staleTime: 0,
    });
    const effectiveUpdatedAt = loadedDetails?.updatedAt ?? updatedAt;
    const selectedAnswerPreview = loadedDetails?.answerPreview ?? answerPreview;
    // The Python REST thread serializers can likely return a non-string `answer_preview`
    // (the decoded `answer` is not type-checked), so guard before calling `.trim()`.
    const effectiveAnswerPreview = typeof selectedAnswerPreview === 'string' ? selectedAnswerPreview : null;
    const formattedRelativeTime = useSidebarHoverCardRelativeTime(effectiveUpdatedAt);
    const effectiveProjectTitle = (loadedDetails?.projectTitle ?? projectTitle)?.trim();
    return (_jsx(SidebarHoverCard, { ...cardProps, open: hoverDetailsOpen, onOpenChange: onOpenChange, headerStart: _jsxs(_Fragment, { children: [_jsxs("span", { className: "flex shrink-0 items-center gap-1", children: [_jsx(Icon, { "aria-hidden": true, icon: getThreadTypeIcon(isAutomation, isComputerTask), size: "tiny" }), $t(getThreadTypeMessage(isAutomation, isComputerTask))] }), effectiveProjectTitle && (_jsx(SidebarHoverCardProjectLabel, { title: effectiveProjectTitle, icon: loadedDetails?.projectIcon ?? projectIcon }))] }), headerEnd: formattedRelativeTime && (_jsx("time", { dateTime: effectiveUpdatedAt ?? undefined, children: formattedRelativeTime })), preview: effectiveAnswerPreview }));
});
//# sourceMappingURL=ThreadHoverDetails.js.map